import * as chrono from 'chrono-node'

export interface NaturalDateMatch {
  /** The detected due date. */
  date: Date
  /** The title with the matched date phrase (and connector word) stripped. */
  cleanedTitle: string
  /** The raw matched phrase, useful for debugging/telemetry. */
  matchedText: string
  /** The phrase names a day ("tomorrow", "Friday", "Oct 3"), not only a time or month. */
  hasDay: boolean
  /** The phrase names a time of day ("at 3pm"). */
  hasTime: boolean
}

const TRAILING_CONNECTOR_REGEX = /\s+(by|on|at|due\s+on|due|for)\s*$/i

/**
 * Detect a trailing natural-language date phrase in a task title.
 *
 * Returns a match only when the date phrase sits at the tail of the input —
 * that is, no further non-whitespace text follows the match. This keeps
 * mid-string edits (e.g. typing in the middle of an existing title) from
 * unexpectedly mutating the value.
 *
 * Caller is responsible for deciding whether to apply the match (e.g. only on
 * user-typed input, not on programmatic title sets).
 */
export function detectTrailingDate(
  title: string,
  refDate: Date = new Date(),
): NaturalDateMatch | null {
  if (!title) return null

  const results = chrono.parse(title, refDate, { forwardDate: true })
  if (results.length === 0) return null

  // Walk results from end to start; first one whose match sits at the trailing
  // edge of the input wins.
  for (const result of [...results].reverse()) {
    const matchEnd = result.index + result.text.length
    const tail = title.slice(matchEnd)

    if (tail.trim() !== '') continue

    const before = title.slice(0, result.index)
    const cleanedBefore = before.replace(TRAILING_CONNECTOR_REGEX, '')
    const cleanedTitle = cleanedBefore.trimEnd()

    return {
      date: result.start.date(),
      cleanedTitle,
      matchedText: result.text,
      hasDay:
        result.start.isCertain('day') || result.start.isCertain('weekday'),
      hasTime: result.start.isCertain('hour'),
    }
  }

  return null
}
